<template>
  <nav>
    <!-- Mobile layout -->
    <div v-if="isMobile" class="flex flex-col mt-4">
      <div class=" flex items-center justify-between pr-4">
        <div class="flex space-x-2">
          <svg @click="showCategoryMenu" class="mt-2 w-4 h-4 text-blue dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 17 14">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 1h15M1 7h15M1 13h15"/>
          </svg>
          <button @click="goToHome" class="text-xl font-extrabold font-serif leading-none tracking-tight text-blue md:text-5xl lg:text-6xl dark:text-white">Scap.</button>
        </div>

        <div class="flex space-x-2 items-center justify-between">
          <!-- Language switcher — light variant for the white mobile AppBar. -->
          <LanguageSwitcher />

          <div v-if="client" class="flex space-x-8">
            <div class="relative hover:rounded-full hover:border-2 hover:border-blue">
              <svg @click="toggleDialog" class="h-8 w-8 text-blue cursor-pointer" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
              <!--
                No extra positioning on the dialog — DialogComponent
                self-manages its mobile layout as a `fixed inset-0`
                full-screen sheet. Earlier wrappers added `absolute
                top-full right-0 mt-60`, which fought with the inner
                template and rendered the menu 240 px below the icon
                anchored to the wrong axis.
              -->
              <DialogComponent v-if="showDialog" @close="showDialog = false" />
            </div>
          </div>
          <div v-else>
            <a href="/logineC" class="capitalize text-blue text-md pl-4 hover:underline">{{ $t('nav.login') }}</a>
          </div>

          <div>
            <a href="/liste">
              <span class="icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                  <path fill="currentColor" fill-rule="evenodd" d="M14.461 2.495a6.5 6.5 0 0 1 8.494 8.494c-.196.472-.514.91-.81 1.264-.305.363-.634.696-.89.953l-8.5 8.498a1 1 0 0 1-1.414 0l-8.498-8.498C1.74 12.103.55 10.354.55 8.5a6.5 6.5 0 0 1 11.097-4.597l.353.353.352-.352a6.5 6.5 0 0 1 2.11-1.41M16.95 4a4.5 4.5 0 0 0-3.182 1.319l-1.06 1.06a1 1 0 0 1-1.414 0l-1.06-1.06A4.5 4.5 0 0 0 2.55 8.5c0 1.065.746 2.33 1.707 3.29l7.791 7.792 7.792-7.791c.255-.255.529-.534.77-.822.249-.298.416-.552.497-.747a4.5 4.5 0 0 0-2.436-5.88A4.5 4.5 0 0 0 16.95 4" clip-rule="evenodd"/>
                </svg>
              </span>
            </a>
          </div>
          <div>
            <a href="/panierPage" :aria-label="$t('nav.myCart')" class="relative inline-block">
              <span class="icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                  <g clip-path="url(#a)">
                    <path fill="currentColor" fill-rule="evenodd" d="M0 1a1 1 0 0 1 1-1h4a1 1 0 0 1 .98.804L6.82 5H23a1 1 0 0 1 .982 1.187l-1.601 8.398A3 3 0 0 1 19.39 17H9.69a3 3 0 0 1-2.99-2.414L5.03 6.239l-.017-.084L4.18 2H1a1 1 0 0 1-1-1m7.22 6 1.44 7.195a1 1 0 0 0 1 .805h9.76a1 1 0 0 0 .999-.802L21.79 7zM7 21a2 2 0 1 1 4 0 2 2 0 0 1-4 0m11 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0" clip-rule="evenodd"/>
                  </g>
                </svg>
              </span>
              <span
                v-if="cartCount > 0"
                class="absolute -top-1 -right-2 h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
              >{{ cartCount > 99 ? '99+' : cartCount }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Desktop layout -->
    <div v-else class="bg-blue w-full px-4 md:px-10 lg:px-40 py-4 border-b border-gray-200 dark:bg-gray-900 flex items-center gap-4">
      <!-- LEFT: Logo -->
      <button @click="goToHome" class="text-2xl md:text-3xl font-extrabold text-white whitespace-nowrap">
        Scap.
      </button>

      <!-- CENTER: Search (absolute center using flex-grow) -->
      <div class="flex-grow flex justify-center">
        <div class="relative w-[70%] min-w-[200px] max-w-[420px]">
          <input
            v-model="searchTerm"
            type="text"
            :placeholder="$t('nav.searchPlaceholder')"
            :aria-label="$t('common.search')"
            class="w-full h-10 md:h-12 pl-4 pr-10 text-sm md:text-lg text-gray-700 bg-white border border-gray-300 rounded-full focus:border-blue transition-all"
            @focus="showSearchResults = true"
            @input="search"
          />

          <svg class="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35m1.8-4.45a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z" />
          </svg>

          <!-- Search Results Dropdown -->
          <div v-if="showSearchResults && searchResults.length > 0" class="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl mt-2 z-50 max-h-96 overflow-y-auto">
            <div class="p-2 space-y-1">
              <div
                v-for="result in searchResults"
                :key="result.id"
                @click="productClicked(result)"
                class="flex items-center space-x-3 p-3 hover:bg-blue-50 rounded-lg cursor-pointer transition-colors duration-200"
              >
                <!-- Product Image -->
                <div class="w-12 h-12 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                  <img
                    :src="getFirstImage(result.images)"
                    :alt="result.nom"
                    class="w-full h-full object-cover"
                  />
                </div>

                <!-- Product Info -->
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 truncate">{{ result.nom }}</p>
                  <p class="text-xs text-gray-500 truncate">{{ result.title }}</p>
                  <div class="flex items-center space-x-2 mt-1">
                    <span class="text-sm font-bold text-red-600">{{ result.prix }} Dhs</span>
                    <div class="flex items-center">
                      <svg
                        v-for="n in 5"
                        :key="n"
                        class="w-3 h-3"
                        :class="n <= Number(result.average_rating) ? 'text-yellow-400' : 'text-gray-300'"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No Results Message -->
          <div v-if="showSearchResults && searchTerm && searchResults.length === 0" class="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl mt-2 z-50 p-4">
            <p class="text-gray-500 text-center">{{ $t('nav.searchNoResults', { term: searchTerm }) }}</p>
          </div>
        </div>
      </div>

      <!-- RIGHT: Language + User + Wishlist + Cart -->
      <div class="flex items-center gap-4 whitespace-nowrap">
        <!-- Language switcher — dark variant for the blue desktop AppBar. -->
        <LanguageSwitcher on-dark />

        <div v-if="client" class="flex items-center gap-2 relative">
          <div class="text-white text-sm leading-tight text-right">
            <p class="font-bold">{{ $t('nav.welcome') }}</p>
            <p class="capitalize font-semibold">{{ client.name }}</p>
          </div>

          <div
            @click="toggleDialog"
            @mouseenter="showDialog = true"
            class="h-10 w-10 flex items-center justify-center cursor-pointer hover:bg-blue-600 rounded-full transition-colors duration-200"
          >
            <svg class="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
            </svg>
          </div>

          <!-- DialogComponent for desktop with hover behavior -->
          <div
            class="absolute top-full right-0 mt-2 z-50"
            @mouseenter="showDialog = true"
            @mouseleave="showDialog = false"
            v-show="showDialog"
          >
            <DialogComponent @close="showDialog = false" />
          </div>
        </div>

        <div v-else>
          <a href="/logineC" class="text-white font-semibold">{{ $t('nav.login') }}</a>
        </div>

        <a href="/liste" class="h-10 w-10 flex items-center justify-center hover:bg-blue-600 rounded-full transition-colors duration-200">
          <svg class="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
          </svg>
        </a>

        <a href="/panierPage" class="relative h-10 w-10 flex items-center justify-center hover:bg-blue-600 rounded-full transition-colors duration-200">
          <svg class="h-8 w-8 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M0 1a1 1 0 011-1h4a1 1 0 01.98.804L6.82 5H23a1 1 0 01.982 1.187l-1.601 8.398A3 3 0 0119.39 17H9.69a3 3 0 01-2.99-2.414L5.03 6.239l-.017-.084L4.18 2H1A1 1 0 010 1zm7.22 6l1.44 7.195a1 1 0 001 .805h9.76a1 1 0 00.999-.802L21.79 7zM7 21a2 2 0 114 0 2 2 0 01-4 0m11 0a2 2 0 114 0 2 2 0 01-4 0"/>
          </svg>
          <span
            v-if="cartCount > 0"
            class="absolute top-0 right-0 h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-blue"
          >{{ cartCount > 99 ? '99+' : cartCount }}</span>
        </a>
      </div>
    </div>

    <!-- Category Menu Dialog -->
    <category-menu
      v-if="showCategoryMenuDialog"
      :showDialog="showCategoryMenuDialog"
      @update:showDialog="showCategoryMenuDialog = $event"
      @close="showCategoryMenuDialog = false"
    ></category-menu>

    <!-- Click Outside Listener for Search Results -->
    <div v-if="showSearchResults" class="fixed inset-0 z-40" @click="showSearchResults = false"></div>
  </nav>
</template>

<script>
import axios from 'axios'
import CategoryMenu from './category/categoryMenu.vue'
import DialogComponent from './DialogComponent.vue'
import LanguageSwitcher from './LanguageSwitcher.vue'

export default {
  components: {
    CategoryMenu,
    DialogComponent,
    LanguageSwitcher,
  },
  data() {
    return {
      searchTerm: '',
      searchResults: [],
      windowWidth: window.innerWidth,
      showCategoryMenuDialog: false,
      showDialog: false,
      showSearchResults: false,
      dialogTimeout: null,
      searchTimeout: null,
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768
    },
    client() {
      return this.$store.state.client;
    },
    cartCount() {
      const achats = this.$store.state.achats;
      return Array.isArray(achats) ? achats.length : 0;
    },
  },

  methods: {
    getFirstImage(images) {
      if (Array.isArray(images) && images.length > 0) {
        return images[0].image;
      }
      return '/default-image.jpg';
    },

    productClicked(product) {
      if (!product?.id) return;
      this.$store.commit('setSelectedProduct', product);
      this.$router.push({ name: 'ProductDetails', params: { id: product.id } });
      this.searchResults = [];
      this.showSearchResults = false;
      this.searchTerm = '';
    },

    goToHome() {
      this.$router.push('/');
    },

    async search() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Set new timeout to debounce search
      this.searchTimeout = setTimeout(async () => {
        if (this.searchTerm.trim().length === 0) {
          this.searchResults = [];
          return;
        }

        try {
          const response = await axios.get(`https://scap.ma/api/search?term=${this.searchTerm}`);
          this.searchResults = JSON.parse(JSON.stringify(response.data));
          this.showSearchResults = true;
        } catch (error) {
          console.error('Search error:', error);
          this.searchResults = [];
        }
      }, 300); // 300ms debounce
    },

    handleResize() {
      this.windowWidth = window.innerWidth
    },

    showCategoryMenu() {
      this.showCategoryMenuDialog = true;
    },

    toggleDialog() {
      this.showDialog = !this.showDialog;
    },

    // Optional: Add delayed hide for better UX
    startHideTimeout() {
      this.dialogTimeout = setTimeout(() => {
        this.showDialog = false;
      }, 300);
    },

    clearHideTimeout() {
      if (this.dialogTimeout) {
        clearTimeout(this.dialogTimeout);
        this.dialogTimeout = null;
      }
    }
  },

  created() {
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('click', this.handleClickOutside);
    if (this.dialogTimeout) {
      clearTimeout(this.dialogTimeout);
    }
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
}
</script>

<style scoped>
.input-group {
  display: flex;
  width: 100%;
}

nav {
  padding: 0.5rem;
}

a span {
  font-size: 1.5rem;
}

@media (max-width: 767px) {
  .icon {
    color: #3B82F6;
  }
}

/* Smooth transitions for dialog */
.dialog-enter-active,
.dialog-leave-active {
  transition: all 0.2s ease;
}

.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Custom scrollbar for search results */
.search-results::-webkit-scrollbar {
  width: 6px;
}

.search-results::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.search-results::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.search-results::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
